// ThisWeekTab — the new home tab (redesign Phase 3a).
//
// Shows the church's current published 5-day devotional (built from last
// Sunday's sermon) front and centre: a sermon row, a gold devotional card
// with M-T-W-T-F progress, a face stack of who's done today's, and the
// church's upcoming events with an "I'm going" toggle.
//
// Phase 3a scope: only the primary "has a church, has a current plan" state.
// The "sermon only" alternate state, the dedicated Sermon Recap screen, and
// the Read/Reflect/Respond devotional-day restyle are all deferred to Phase
// 3b — tapping the sermon row opens the existing FullSermonSheet directly
// for now (its own "Full notes" button will become the Recap screen's entry
// point once that exists).
//
// The "no church yet" state (built in redesign Phase 8a, per
// docs/designs/WeekNoChurch.dc.html) is handled directly in this file: an
// inline find-your-church search (joins straight away, or "Request to join"
// for invite-only churches, reusing the same access_requests flow as
// Onboarding.jsx's find-church step), a static "while you look" nudge into
// the Gospel of John, and a "can't see your church?" card. The mockup's
// second "I'm a church leader" row is intentionally omitted — it points at
// the gated Part B self-serve leader flow, which isn't built.
//
// Props:
//   churchId, userId, profile, isModerator
//   onOpenPlanDay(plan, follow, dayNumber) — open ReadingPlanDaySheet
//   onOpenSermon(sermon)                   — open FullSermonSheet
//   onOpenLeaders()                        — open LeadersPage (redesign Phase 7)
//   onOpenVerse(book, chapter, verse)       — open the Bible tab at a verse
//   onAddEvent()                           — open EventEditorSheet, blank (elders/admins)
//   onEditEvent(event, occurrenceDate)     — open EventEditorSheet for one occurrence (elders/admins)
//   onDeleteEvent(event, occurrenceDate)   — open EventEditorSheet straight at its delete
//                                             confirm/scope-choice flow (elders/admins)
//   onEventsRefresh(refreshFn)             — hands this tab's useEvents() refresh up to
//                                             BibleApp.jsx, so EventEditorSheet (mounted
//                                             there) can refetch after a save/delete
//   onPlanRefresh(refreshFn)               — same pattern, for the plan/follow load() —
//                                             BibleApp.jsx's own ReadingPlanDaySheet mount
//                                             (opened via onOpenPlanDay) tracks its own copy
//                                             of `follow`, so leaving/joining/finishing a day
//                                             there needs this to keep the card in sync
//   onOpenBible(book?, chapter?)           — switch to the Bible tab; with no args, resumes
//                                             wherever the user last was (BibleApp.jsx's own
//                                             restore); with args, jumps to that chapter
//   onOpenCommunity()                      — switch to the Community tab
//   onOpenCommunityPrayer()                — switch to Community tab, Prayer chip (from
//                                             PrayerListSheet's "See all … requests")
//   prayerListSignal                       — bump to force-open PrayerListSheet (e.g. from
//                                             a prayer_answered notification), same pattern
//                                             as ProfileSheet's notifPrefsSignal
// (Past-plans browsing lives in the Sermons tab as of Phase 4 — this screen
// no longer needs an "All plans" escape hatch.)

import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../supabase";
import { useAuth } from "../hooks/useAuth";
import { useEvents } from "../hooks/useEvents";
import { usePrayerListCounts } from "../hooks/useData";
import { shareChurchInvite, shareTextViaBestChannel } from "../lib/shareInvite";
import { displayTitleOf } from "../lib/sermonSeries";
import FaceStack from "./FaceStack";
import ReadingPlanReviewSheet from "./ReadingPlanReviewSheet";
import PrayerListSheet from "./PrayerListSheet";
import AllEventsSheet from "./AllEventsSheet";
import EventRow, { eventEditPayload } from "./EventRow";
import { fmtClock } from "./YouTubeEmbed";
import { PlayBadge } from "./PlayIcon";

const GOLD = "var(--gold)";
const SANS = "'Plus Jakarta Sans', system-ui, sans-serif";

// First-visit devotional-card pulse (redesign Phase 8a) — plays once per
// user, then never again. Scoped per-user in localStorage like the app's
// other readUserScoped-style flags.
function hasSeenFirstDevoCard(userId) {
  if (typeof window === "undefined" || !userId) return true;
  try { return localStorage.getItem(`nlc.sawFirstDevoCard:${userId}`) === "1"; }
  catch { return true; }
}
function markSeenFirstDevoCard(userId) {
  if (typeof window === "undefined" || !userId) return;
  try { localStorage.setItem(`nlc.sawFirstDevoCard:${userId}`, "1"); } catch {}
}

function leaderSummaryText({ reports, plans, access }) {
  const parts = [];
  if (reports > 0) parts.push(`${reports} ${reports === 1 ? "report" : "reports"}`);
  if (plans > 0) parts.push(`${plans} ${plans === 1 ? "plan" : "plans"} to approve`);
  if (access > 0) parts.push(`${access} ${access === 1 ? "person" : "people"} asking to join`);
  return parts.join(" · ");
}

// The sermon's actual main passage, not just whatever ref happens to be
// first — used for both the sermon card's "[passage] · Recap →" line and
// "This week's verse", so the two never disagree (docs/FIX-SERMON-CLIPS.md §3c).
function mainRefOf(sermon) {
  const refs = sermon?.sermon_refs || [];
  return refs.find(r => r.kind === "main_passage") || refs[0] || null;
}


// Mirrors BibleApp.jsx's own "nlc.lastPlace" read — a read-only snapshot for
// the "Keep reading" card's label. Falls back to the same Romans 8 default
// BibleApp.jsx uses for first-time visits.
function readLastPlace() {
  if (typeof window === "undefined") return { book: "Romans", chap: 8 };
  try {
    const raw = JSON.parse(localStorage.getItem("nlc.lastPlace") || "{}");
    if (raw.book && raw.chap) return { book: raw.book, chap: raw.chap };
  } catch {}
  return { book: "Romans", chap: 8 };
}

export default function ThisWeekTab({ churchId, churchName, userId, isModerator, onOpenPlanDay, onOpenSermon, onOpenLeaders, onOpenVerse, onAddEvent, onEditEvent, onDeleteEvent, onEventsRefresh, onPlanRefresh, onOpenBible, onOpenCommunity, onOpenCommunityPrayer, prayerListSignal, onSaveVerse }) {
  const { requestAccess, refreshProfile } = useAuth();
  const [plan, setPlan] = useState(null);
  const [follow, setFollow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [totalJoined, setTotalJoined] = useState({ profiles: [], total: 0 });
  const [leaderCounts, setLeaderCounts] = useState({ reports: 0, plans: 0, access: 0 });
  // Add/Edit/Delete on events sit behind the cog, off by default, rather
  // than Edit/Delete always showing on every row.
  const [manageEventsOpen, setManageEventsOpen] = useState(false);
  // Next 3, then "See all events →" opens AllEventsSheet (docs/designs/AllEvents.png).
  const [allEventsOpen, setAllEventsOpen] = useState(false);
  // "I'm going" failures used to be silent (console.warn only) — this
  // surfaces them right on the row that failed, for a few seconds.
  const [rsvpError, setRsvpError] = useState(null); // { key, message } | null
  const [prayerListOpen, setPrayerListOpen] = useState(false);
  const { toPray: prayersToPray, answered: prayersAnswered } = usePrayerListCounts();
  useEffect(() => { if (prayerListSignal != null) setPrayerListOpen(true); }, [prayerListSignal]);

  async function handleToggleGoing(ev) {
    const key = `${ev.id}-${ev.occurrenceDate}`;
    setRsvpError(null);
    const { error } = await toggleGoing(ev.id, ev.occurrenceDate, ev.going);
    if (error) setRsvpError({ key, message: error });
  }
  const [pulseDevo, setPulseDevo] = useState(false);
  const [standaloneSermon, setStandaloneSermon] = useState(null);

  const { events, error: eventsError, toggleGoing, refresh: refreshEvents } = useEvents(churchId);

  // Hand the refresh fn up to BibleApp.jsx, which mounts EventEditorSheet
  // (shared with church settings) outside this tab.
  useEffect(() => { onEventsRefresh?.(refreshEvents); }, [onEventsRefresh, refreshEvents]);

  // First-visit pulse on the devotional (or sermon, if no devotional yet)
  // card — plays once, then marks itself seen so it never repeats.
  useEffect(() => {
    if (loading || !churchId) return;
    if (hasSeenFirstDevoCard(userId)) return;
    setPulseDevo(true);
    markSeenFirstDevoCard(userId);
  }, [loading, churchId, userId]);

  // "For leaders" summary card counts — cheap count-only queries, safe to
  // run on every This Week visit. The full list views live in LeadersPage.
  useEffect(() => {
    if (!isModerator || !churchId) { setLeaderCounts({ reports: 0, plans: 0, access: 0 }); return; }
    let cancelled = false;
    (async () => {
      const [reportsRes, plansRes, accessRes] = await Promise.all([
        supabase.from("reports").select("id", { count: "exact", head: true }).eq("church_id", churchId).eq("resolved", false),
        supabase.from("reading_plans").select("id", { count: "exact", head: true }).eq("church_id", churchId).in("status", ["draft", "approved"]),
        supabase.from("access_requests").select("id", { count: "exact", head: true }).eq("church_id", churchId).eq("status", "pending"),
      ]);
      if (cancelled) return;
      setLeaderCounts({ reports: reportsRes.count || 0, plans: plansRes.count || 0, access: accessRes.count || 0 });
    })();
    return () => { cancelled = true; };
  }, [isModerator, churchId]);

  const load = useCallback(async () => {
    if (!churchId) { setPlan(null); setFollow(null); setLoading(false); return; }
    setLoading(true);
    let { data: planRow, error: planErr } = await supabase
      .from("reading_plans")
      .select("*, sermons(id, title, display_title, preacher, date, youtube_id, youtube_url, duration, church_id, sermon_refs(*)), reading_plan_days(*)")
      .eq("church_id", churchId)
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    // Tolerate display_title not existing yet (migration not run) — retry
    // without it rather than silently showing "no plan" for everyone.
    if (planErr && /display_title/i.test(planErr.message || "")) {
      ({ data: planRow } = await supabase
        .from("reading_plans")
        .select("*, sermons(id, title, preacher, date, youtube_id, youtube_url, duration, church_id, sermon_refs(*)), reading_plan_days(*)")
        .eq("church_id", churchId)
        .eq("status", "published")
        .order("published_at", { ascending: false })
        .limit(1)
        .maybeSingle());
    }
    if (planRow) {
      planRow.reading_plan_days = (planRow.reading_plan_days || []).slice().sort((a, b) => a.day_number - b.day_number);
    }
    setPlan(planRow || null);

    let followRow = null;
    if (planRow && userId) {
      const { data } = await supabase
        .from("reading_plan_follows")
        .select("*")
        .eq("plan_id", planRow.id)
        .eq("user_id", userId)
        .maybeSingle();
      followRow = data || null;
    }
    setFollow(followRow);
    setLoading(false);
  }, [churchId, userId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { onPlanRefresh?.(load); }, [onPlanRefresh, load]);

  // No published plan — fall back to the latest sermon on its own, so This
  // Week isn't just an empty message (Sermon-only state, docs/designs/WeekSermon).
  useEffect(() => {
    if (loading || plan || !churchId) { setStandaloneSermon(null); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("sermons")
        .select("id, title, display_title, preacher, date, youtube_id, youtube_url, duration, church_id, sermon_refs(*)")
        .eq("church_id", churchId)
        .order("date", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!cancelled) setStandaloneSermon(data || null);
    })();
    return () => { cancelled = true; };
  }, [loading, plan, churchId]);

  // Any-status reading_plans row for the standalone sermon — not just
  // published (that's what `plan` already covers) — so the "For leaders"
  // banner doesn't re-offer generation when a draft is already mid-review.
  // Defaults to true (assume a plan exists) so the banner never flashes on
  // first render, only once we actually know there's nothing yet.
  const [standaloneSermonHasPlan, setStandaloneSermonHasPlan] = useState(true);
  useEffect(() => {
    let cancelled = false;
    if (!standaloneSermon?.id) { setStandaloneSermonHasPlan(true); return; }
    supabase.from("reading_plans").select("id", { count: "exact", head: true }).eq("sermon_id", standaloneSermon.id)
      .then(({ count }) => { if (!cancelled) setStandaloneSermonHasPlan((count || 0) > 0); });
    return () => { cancelled = true; };
  }, [standaloneSermon?.id]);

  // "This week's verse" — same week's sermon regardless of plan state (see
  // weekSermon below), so it keeps showing once a plan is published, not
  // just in the no-plan fallback. Verse text fetched the same lightweight
  // way BibleApp.jsx's fetchSingleVerseText does (public-domain fallback,
  // no auth needed) — this is the only other call site, so inlined rather
  // than shared.
  const [weekVerseText, setWeekVerseText] = useState("");
  useEffect(() => {
    const ref = mainRefOf(plan?.sermons || standaloneSermon);
    if (!ref) { setWeekVerseText(""); return; }
    let cancelled = false;
    (async () => {
      try {
        const bookApi = (ref.book || "").toLowerCase().replace(/\s+/g, "+");
        const r = await fetch(`https://bible-api.com/${bookApi}+${ref.chapter}:${ref.verse}?translation=web`);
        if (!r.ok) { if (!cancelled) setWeekVerseText(""); return; }
        const d = await r.json();
        const t = String(d.text || d.verses?.[0]?.text || "").replace(/\s+/g, " ").trim();
        // A complete verse, never a fragment or cross-reference — a cheap
        // proxy is that it starts with a capital letter (docs/FIX-SERMON-CLIPS.md §3c).
        if (!cancelled) setWeekVerseText(/^[A-Z]/.test(t) ? t : "");
      } catch { if (!cancelled) setWeekVerseText(""); }
    })();
    return () => { cancelled = true; };
  }, [plan?.sermons, standaloneSermon]);

  // "Create it" — fetch the full sermon row (standaloneSermon deliberately
  // excludes `transcript`, which generateReadingPlan needs) and open the same
  // review sheet FullSermonSheet/PlansTab already use to generate+approve.
  const [creatingPlanTarget, setCreatingPlanTarget] = useState(null); // { sermon, sermonRefs } | null
  const [creatingPlanLoading, setCreatingPlanLoading] = useState(false);
  async function handleCreateIt() {
    if (!standaloneSermon?.id || creatingPlanLoading) return;
    setCreatingPlanLoading(true);
    const { data: fullSermon } = await supabase.from("sermons").select("*").eq("id", standaloneSermon.id).single();
    setCreatingPlanLoading(false);
    if (fullSermon) setCreatingPlanTarget({ sermon: fullSermon, sermonRefs: standaloneSermon.sermon_refs || [] });
  }
  function refreshAfterPlanSave() {
    setCreatingPlanTarget(null);
    load();
    if (standaloneSermon?.id) {
      supabase.from("reading_plans").select("id", { count: "exact", head: true }).eq("sermon_id", standaloneSermon.id)
        .then(({ count }) => setStandaloneSermonHasPlan((count || 0) > 0));
    }
  }

  const completedSet = useMemo(() => new Set(follow?.days_completed || []), [follow]);
  const allDone = completedSet.size >= 5;
  // A follow row with left_at set (docs/FIX-PLANS.md §1i) means they left —
  // the row stays (days_completed is kept for a future rejoin) but it's not
  // an active join for display purposes.
  const isJoined = !!follow && !follow.left_at;
  const hasLeftPlan = !!follow?.left_at;

  // The day to work on next — earliest not-yet-completed, never "missed".
  // There is no day-locking, no "behind" state and no red/dashed days
  // anywhere (docs/FIX-PLANS.md principles) — skipping a day just means
  // the next day is "ready when you are".
  function earliestUnfinishedDay() {
    for (let n = 1; n <= 5; n++) if (!completedSet.has(n)) return n;
    return 1;
  }
  const nextDay = earliestUnfinishedDay();

  // Status line + button text for the dark plan card (docs/FIX-PLANS.md
  // §1h) — no "behind"/"missed"/"1 done" anywhere. The exact "today's day
  // just finished" nuance in the design isn't derivable from stored data
  // (days_completed has no per-day timestamp), so a day that's done reads
  // the same whether it was finished moments ago or last week — never
  // wrong, just not maximally specific.
  const planStatusLine = allDone
    ? "Plan finished · well done"
    : completedSet.size === 0
      ? "5 short readings · at your own pace"
      : completedSet.size === nextDay - 1
        ? `Day ${nextDay} of 5 · Day ${nextDay - 1} done`
        : `Day ${nextDay} of 5 · ${completedSet.size} done`;
  const planOpenLabel = !isJoined ? "Join" : "Open ›";
  const planOpenOutline = allDone;

  // Who's joined (not just who did today) — docs/designs/PlanOpt2a.dc.html /
  // PlanOpt2b.dc.html's "23 from New Life joined" / "You + 23 others". A
  // SECURITY DEFINER RPC, not a direct table read, since reading_plan_follows
  // is deliberately own-row-only RLS.
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!plan?.id) { setTotalJoined({ profiles: [], total: 0 }); return; }
      const { data, error } = await supabase.rpc("reading_plan_total_joined", { p_plan_id: plan.id, p_limit: 3 });
      if (cancelled) return;
      if (error) { console.warn("reading_plan_total_joined:", error.message); setTotalJoined({ profiles: [], total: 0 }); return; }
      setTotalJoined({ profiles: data || [], total: (data && data[0]?.total_count) || 0 });
    }
    run();
    return () => { cancelled = true; };
  }, [plan?.id]);


  async function startPlan() {
    // A `follow` row with `left_at` set (docs/FIX-PLANS.md §1i) means they
    // left, not that they're currently joined — only an active follow
    // should block starting again.
    if (!userId || !plan || (follow && !follow.left_at) || starting) return;
    setStarting(true);
    const { data } = await supabase
      .from("reading_plan_follows")
      // Rejoining clears left_at but leaves days_completed untouched
      // (upsert only overwrites the columns listed here) — §1i's "progress
      // is kept".
      .upsert({ plan_id: plan.id, user_id: userId, last_opened_day: 1, left_at: null }, { onConflict: "plan_id,user_id" })
      .select().single();
    setFollow(data);
    setStarting(false);
    onOpenPlanDay?.(plan, data, 1);
  }

  // ── "No church yet" find-your-church search (redesign Phase 8a) ──────
  const [noChurchSearch, setNoChurchSearch] = useState("");
  const [noChurchResults, setNoChurchResults] = useState([]);
  const [joiningChurchId, setJoiningChurchId] = useState(null);
  const [requestedChurchIds, setRequestedChurchIds] = useState(() => new Set());

  useEffect(() => {
    if (churchId) return;
    const q = noChurchSearch.trim();
    if (!q) { setNoChurchResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      const { data } = await supabase.from("churches")
        .select("id, name, location, invite_only")
        .or(`name.ilike.%${q}%,location.ilike.%${q}%`)
        .limit(8);
      if (!cancelled) setNoChurchResults(data || []);
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [noChurchSearch, churchId]);

  async function joinChurch(c) {
    if (!userId || joiningChurchId) return;
    setJoiningChurchId(c.id);
    await supabase.from("profiles").update({ church_id: c.id }).eq("id", userId);
    await supabase.from("user_church_follows").upsert({ user_id: userId, church_id: c.id }, { onConflict: "user_id,church_id" });
    await refreshProfile?.();
    setJoiningChurchId(null);
  }
  async function requestToJoinChurch(c) {
    if (requestedChurchIds.has(c.id)) return;
    setRequestedChurchIds(prev => new Set(prev).add(c.id));
    await requestAccess?.("membership", c.id);
  }

  const totalJoinedProfilesById = useMemo(() => {
    const m = {};
    for (const p of totalJoined.profiles) m[p.user_id] = { full_name: p.full_name, avatar_url: p.avatar_url };
    return m;
  }, [totalJoined.profiles]);

  if (loading) {
    return <div style={S.wrap}><div style={S.empty}>Loading…</div></div>;
  }

  if (!churchId) {
    return (
      <div style={S.wrap}>
        <div style={S.noChurchCard}>
          <div style={S.noChurchStep}>STEP 1</div>
          <div style={S.noChurchHeading}>Find your church</div>
          <div style={S.noChurchSub}>Get their Sunday sermons, weekly reading plans and community in one place.</div>
          <input
            style={S.noChurchSearchInput}
            placeholder="Church name or town"
            value={noChurchSearch}
            onChange={e => setNoChurchSearch(e.target.value)}
          />
          {noChurchResults.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
              {noChurchResults.map(c => {
                const requested = requestedChurchIds.has(c.id);
                const joining = joiningChurchId === c.id;
                return (
                  <div key={c.id} style={S.noChurchResultRow}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={S.noChurchResultName}>{c.name}</div>
                      {c.location && <div style={S.noChurchResultMeta}>{c.location}</div>}
                    </div>
                    {c.invite_only ? (
                      <button style={S.noChurchRequestBtn} disabled={requested} onClick={() => requestToJoinChurch(c)}>
                        {requested ? "Requested ✓" : "Request"}
                      </button>
                    ) : (
                      <button style={S.noChurchJoinBtn} disabled={joining} onClick={() => joinChurch(c)}>
                        {joining ? "…" : "Join"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={S.whileLookSection}>
          <div style={S.sectionTitle}>While you look</div>
          <button style={S.johnCard} onClick={() => onOpenVerse?.("John", 1, 1)}>
            <div style={S.johnIcon}>📖</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.johnTitle}>Start with the Gospel of John</div>
              <div style={S.johnMeta}>A great first book · 21 short chapters</div>
            </div>
          </button>
        </div>

        <div style={S.cantFindCard}>
          <div style={S.cantFindHeading}>Can't see your church?</div>
          <button style={S.inviteLeaderRow} onClick={() => shareChurchInvite()}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.inviteLeaderTitle}>Invite your church leader</div>
              <div style={S.inviteLeaderMeta}>Send them a ready-made message</div>
            </div>
            <span aria-hidden="true" style={{ fontWeight: 800 }}>→</span>
          </button>
        </div>
      </div>
    );
  }

  const sermon = plan?.sermons;
  const leaderTotal = leaderCounts.reports + leaderCounts.plans + leaderCounts.access;
  const lastPlace = readLastPlace();

  // One sermon source regardless of plan state (docs/designs/ThisWeekStates.png:
  // the page layout is identical across every state) — the published plan's
  // sermon when there is one, else the latest standalone sermon.
  const weekSermon = sermon || standaloneSermon;
  // One canonical main-passage ref, shared by the sermon card's meta line and
  // "This week's verse" — never two different Bible references on the page
  // (docs/FIX-SERMON-CLIPS.md §3c).
  const weekRef = mainRefOf(weekSermon);
  const thumbBase = weekSermon?.youtube_id ? `https://i.ytimg.com/vi/${weekSermon.youtube_id}` : null;
  // Elder viewing a sermon with no plan yet AND nothing already mid-review
  // (a draft/approved-but-unpublished plan existing shouldn't re-offer
  // "Create it" — standaloneSermonHasPlan covers any status, not just published).
  const showCreateItStrip = isModerator && !plan && !!standaloneSermon && !standaloneSermonHasPlan;

  return (
    <div style={S.wrap}>
      <style>{"@keyframes tw-pulse{0%,100%{box-shadow:0 0 0 0 rgba(232,200,122,0)} 50%{box-shadow:0 0 0 10px rgba(232,200,122,0.35)}} .tw-pulse{animation:tw-pulse 1.6s ease-in-out 2}"}</style>

      {/* "For leaders" pill — white now (was ink-filled), independent of
          plan/join state (docs/FIX-PLANS.md §1h). */}
      {isModerator && leaderTotal > 0 && (
        <button type="button" style={S.leaderCard} onClick={onOpenLeaders}>
          <span style={S.leaderPillEyebrow}>For leaders</span>
          <span style={S.leaderPillSummaryLight}>· {leaderSummaryText(leaderCounts)}</span>
          <span style={S.leaderPillChevronLight}>›</span>
        </button>
      )}

      {/* Dark plan card (docs/FIX-PLANS.md §1h, designs/PlanCardDark.png
          option C) — replaces the old merged sermon+plan card. No day title
          or passage on the card itself; those show once you tap in. */}
      {plan && plan.reading_plan_days?.length > 0 && (
        hasLeftPlan ? (
          <div style={S.planLeftRow}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={S.planLeftKicker}>THIS WEEK'S BIBLE PLAN</div>
              <div style={S.planLeftTitle}>{displayTitleOf(plan.sermons) || plan.title} · 5 days</div>
            </div>
            <button type="button" style={S.planLeftJoinBtn} disabled={starting} onClick={startPlan}>{starting ? "…" : "Join"}</button>
          </div>
        ) : (
          <div style={S.darkPlanCard} className={pulseDevo ? "tw-pulse" : undefined}>
            <div style={S.darkKicker}>THIS WEEK'S BIBLE PLAN <span style={S.darkKickerSub}>· from Sunday's sermon</span></div>
            <div style={S.darkTitleRow}>
              <div style={S.darkTitle}>{displayTitleOf(plan.sermons)}</div>
              <button type="button" style={{ ...S.darkOpenBtn, ...(planOpenOutline ? S.darkOpenBtnOutline : null) }}
                onClick={() => isJoined ? onOpenPlanDay?.(plan, follow, nextDay) : startPlan()}
                disabled={starting}>
                {starting ? "…" : planOpenLabel}
              </button>
            </div>
            <div style={S.darkBar}>
              {[1, 2, 3, 4, 5].map(n => (
                <div key={n} style={{
                  ...S.darkBarSegment,
                  ...(completedSet.has(n) ? S.darkBarSegmentDone : n === nextDay && isJoined ? S.darkBarSegmentCurrent : S.darkBarSegmentFuture),
                }}>
                  {completedSet.has(n) && <span style={S.darkBarTick}>✓</span>}
                </div>
              ))}
            </div>
            <div style={S.darkStatusRow}>
              <span style={S.darkStatusLine}>{isJoined ? planStatusLine : "5 short readings · at your own pace"}</span>
              {totalJoined.total > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  <FaceStack userIds={totalJoined.profiles.map(p => p.user_id)} profilesById={totalJoinedProfilesById} size={20} ringColor="#1F1B16"/>
                  <span style={S.darkStatusFaces}>You + {Math.max(0, totalJoined.total - 1)}</span>
                </div>
              )}
            </div>
          </div>
        )
      )}

      {/* Elder, no plan yet — restyled to the dark card (docs/FIX-PLANS.md §1h). */}
      {!plan && showCreateItStrip && (
        <div style={S.darkPlanCard}>
          <div style={S.darkKicker}>FOR LEADERS</div>
          <div style={S.darkTitleRow}>
            <div style={S.darkTitle}>No plan for this sermon yet</div>
            <button type="button" style={S.darkOpenBtn} disabled={creatingPlanLoading} onClick={handleCreateIt}>
              {creatingPlanLoading ? "…" : "Create it"}
            </button>
          </div>
          <div style={S.darkStatusLine}>Members see it once you publish</div>
        </div>
      )}

      {/* Member, no plan — same dark-card treatment, pointing at the passage
          directly rather than a plan to join. */}
      {!plan && !isModerator && !showCreateItStrip && weekRef && (
        <button type="button" style={{ ...S.darkPlanCard, width: "100%", textAlign: "left", border: "none", cursor: "pointer", fontFamily: SANS }}
          onClick={() => onOpenBible?.(weekRef.book, weekRef.chapter)}>
          <div style={S.darkKicker}>THIS WEEK'S BIBLE PLAN</div>
          <div style={S.darkTitleRow}>
            <div style={S.darkTitle}>Read the passage</div>
            <span style={{ ...S.darkOpenBtn, ...S.darkOpenBtnOutline }}>{weekRef.book} {weekRef.chapter} →</span>
          </div>
          <div style={S.darkStatusLine}>No reading plan this week</div>
        </button>
      )}

      {/* "Catch up on Sunday" — the sermon row, separated from the plan card
          entirely now (docs/FIX-PLANS.md §1h). Doesn't repeat the sermon
          title, since the plan card (or the Sermons tab, if there's no
          plan) already shows it. */}
      {weekSermon && (() => {
        const durationSecs = Number(weekSermon.duration) || 0;
        const refCount = (weekSermon.sermon_refs || []).length;
        return (
          <button type="button" style={S.catchUpRow} onClick={() => onOpenSermon?.(weekSermon)}>
            <div style={S.sermonThumbWrap}>
              {thumbBase ? (
                <img
                  src={`${thumbBase}/maxresdefault.jpg`}
                  alt=""
                  style={S.sermonThumb}
                  onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = `${thumbBase}/hqdefault.jpg`; }}
                />
              ) : <div style={{ ...S.sermonThumb, background: "var(--border)" }}/>}
              <div style={S.playDot}><PlayBadge size={28}/></div>
              {durationSecs > 0 && <div style={S.durationBadge}>{fmtClock(durationSecs)}</div>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.sermonTitle}>Catch up on Sunday</div>
              {durationSecs > 0 && <div style={S.sermonMeta}>Full sermon · {Math.round(durationSecs / 60)} min</div>}
              {weekSermon.ai_summary && (
                <div style={S.sermonMeta}>Summary{refCount > 0 ? ` · ${refCount} Bible ref${refCount === 1 ? "" : "s"}` : ""}</div>
              )}
            </div>
            <span style={S.catchUpChevron}>›</span>
          </button>
        );
      })()}

      {/* This week's verse — always shown when there's a complete verse,
          regardless of plan/join state (docs/designs/ThisWeekStates.png) —
          but hidden entirely if weekVerseText failed its validity check
          (fragment/cross-reference), not just left blank. */}
      {weekRef && weekVerseText && (
        <div style={S.verseCard}>
          <div style={S.verseCardHead}>
            <div style={S.verseCardLabel}>This week's verse</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" aria-label="Save verse" style={S.verseIconBtn}
                onClick={() => onSaveVerse?.(weekRef.book, weekRef.chapter, weekRef.verse, weekVerseText)}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z"/></svg>
              </button>
              <button type="button" aria-label="Share verse" style={S.verseIconBtn}
                onClick={() => shareTextViaBestChannel(`"${weekVerseText}" — ${weekRef.verse_ref || `${weekRef.book} ${weekRef.chapter}:${weekRef.verse}`}`)}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7M16 6l-4-4-4 4M12 2v14"/></svg>
              </button>
            </div>
          </div>
          <div style={S.verseCardText}>“{weekVerseText}”</div>
          <div style={S.verseCardRef}>{weekRef.verse_ref || `${weekRef.book} ${weekRef.chapter}:${weekRef.verse}`}</div>
        </div>
      )}

      <div style={S.eventsSection}>
        <div style={S.eventsSectionHead}>
          <div style={S.sectionTitle}>This week's events</div>
          {isModerator && (
            <button type="button" aria-label="Manage events" aria-pressed={manageEventsOpen}
              style={{ ...S.cogBtn, ...(manageEventsOpen ? S.cogBtnOn : null) }}
              onClick={() => setManageEventsOpen(o => !o)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </button>
          )}
        </div>
        {manageEventsOpen && isModerator && (
          <button type="button" style={S.addEventBtn} onClick={() => onAddEvent?.()}>+ Add event</button>
        )}
        {eventsError && (
          <div style={S.eventsErrorCard}>Couldn't load events: {eventsError}</div>
        )}
        {!eventsError && events.length === 0 && (
          <div style={S.noEventsCard}>
            <div style={S.noEventsIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--stone)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="3"/><path d="M16 2v4M8 2v4M3 10h18" stroke="#C8372A"/></svg>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={S.noEventsTitle}>Nothing on this week</div>
              <div style={S.noEventsSub}>Add your Sunday service, groups or prayer meetings. Repeating events are fine.</div>
            </div>
          </div>
        )}
        {events.slice(0, 3).map(ev => (
          <EventRow key={`${ev.id}-${ev.occurrenceDate}`} ev={ev}
            onToggleGoing={handleToggleGoing} rsvpError={rsvpError}
            showManage={isModerator && manageEventsOpen}
            onEdit={isModerator ? e => onEditEvent?.(eventEditPayload(e), e.occurrenceDate) : null}
            onDelete={isModerator ? e => onDeleteEvent?.(eventEditPayload(e), e.occurrenceDate) : null}
          />
        ))}
        {events.length > 3 && (
          <button type="button" style={S.seeAllEventsBtn} onClick={() => setAllEventsOpen(true)}>
            See all events →
          </button>
        )}
      </div>

      <AllEventsSheet open={allEventsOpen} onClose={() => setAllEventsOpen(false)}
        churchId={churchId} isModerator={isModerator}
        onAddEvent={onAddEvent} onEditEvent={onEditEvent}
      />

      {/* Keep reading / Pause & pray — always last, regardless of plan state.
          Equal 1fr/1fr tiles, same vertical-stack layout (docs/designs/ThisWeekFixed.png):
          icon tile on top, bold title, grey subtitle below. */}
      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <button type="button" style={S.keepReadingCard} onClick={() => onOpenBible?.()}>
          <div style={S.tileIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--sky-ink)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M4 4.5A2.5 2.5 0 0 1 6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15z"/></svg>
          </div>
          <div style={S.keepReadingTitle}>Keep reading</div>
          <div style={S.keepReadingSub}>{lastPlace.book} {lastPlace.chap} · where you left off</div>
        </button>
        <button type="button" style={S.prayCard} onClick={() => setPrayerListOpen(true)}>
          <div style={{ ...S.tileIcon, background: "var(--white)" }}>🙏</div>
          <div style={S.prayTitle}>Your prayer list</div>
          <div style={S.praySub}>
            {prayersToPray === 0 && prayersAnswered === 0
              ? "Start your prayer list"
              : prayersToPray === 0
                ? "All caught up"
                : `${prayersToPray} to pray for`}
          </div>
        </button>
      </div>

      {creatingPlanTarget && (
        <ReadingPlanReviewSheet
          sermon={creatingPlanTarget.sermon}
          sermonRefs={creatingPlanTarget.sermonRefs}
          existingPlan={null}
          userId={userId}
          onClose={() => setCreatingPlanTarget(null)}
          onSaved={refreshAfterPlanSave}
        />
      )}

      <PrayerListSheet
        open={prayerListOpen}
        onClose={() => setPrayerListOpen(false)}
        churchName={churchName}
        onOpenCommunityPrayer={() => { setPrayerListOpen(false); onOpenCommunityPrayer?.(); }}
      />
    </div>
  );
}

const S = {
  wrap: { padding: "16px 22px calc(env(safe-area-inset-bottom) + 24px)", fontFamily: SANS, overflowX: "hidden" },
  empty: { fontSize: 13.5, color: "var(--stone)", padding: "30px 4px", textAlign: "center" },

  noChurchCard: { borderRadius: 28, background: "var(--peach)", padding: 20, display: "flex", flexDirection: "column", gap: 12 },
  noChurchStep: { fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--peach-ink)", opacity: 0.75 },
  noChurchHeading: { fontSize: 23, fontWeight: 800, letterSpacing: "-0.01em", color: "var(--peach-ink)", maxWidth: 240, lineHeight: 1.2 },
  noChurchSub: { fontSize: 13.5, lineHeight: 1.45, maxWidth: 240, color: "var(--peach-ink)", opacity: 0.85 },
  noChurchSearchInput: { background: "var(--white)", border: "none", borderRadius: 999, padding: "12px 16px", fontSize: 14.5, color: "var(--ink)", fontFamily: SANS, outline: "none", boxSizing: "border-box" },
  noChurchResultRow: { display: "flex", alignItems: "center", gap: 10, background: "var(--white)", borderRadius: 16, padding: "10px 12px" },
  noChurchResultName: { fontSize: 14, fontWeight: 700, color: "var(--ink)" },
  noChurchResultMeta: { fontSize: 12, color: "var(--stone)", marginTop: 1 },
  noChurchJoinBtn: { flexShrink: 0, background: "var(--ink)", color: "var(--white)", border: "none", borderRadius: 999, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS },
  noChurchRequestBtn: { flexShrink: 0, background: "transparent", border: "1px solid var(--border-strong)", color: "var(--ink)", borderRadius: 999, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS },

  whileLookSection: { marginTop: 26 },
  johnCard: { width: "100%", display: "flex", gap: 14, alignItems: "center", background: "var(--sky)", border: "none", borderRadius: 24, padding: 14, cursor: "pointer", textAlign: "left", fontFamily: SANS },
  johnIcon: { flexShrink: 0, width: 50, height: 50, borderRadius: 16, background: "var(--white)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 },
  johnTitle: { fontSize: 16, fontWeight: 700, color: "var(--sky-ink)" },
  johnMeta: { fontSize: 12.5, color: "var(--sky-ink)", opacity: 0.75, marginTop: 2 },

  cantFindCard: { marginTop: 22, borderRadius: 22, border: "2px dashed var(--border-strong)", padding: 12, display: "flex", flexDirection: "column", gap: 8 },
  cantFindHeading: { fontSize: 14.5, fontWeight: 800, color: "var(--ink)" },
  inviteLeaderRow: { display: "flex", gap: 12, alignItems: "center", background: "var(--sage)", color: "var(--sage-ink)", border: "none", borderRadius: 16, padding: "10px 12px", cursor: "pointer", textAlign: "left", fontFamily: SANS },
  inviteLeaderTitle: { fontSize: 14, fontWeight: 700 },
  inviteLeaderMeta: { fontSize: 12, opacity: 0.85, marginTop: 1 },

  // White pill, thin border, gold label (docs/FIX-PLANS.md §1h — was
  // ink-filled).
  leaderCard: { width: "100%", display: "flex", alignItems: "center", gap: 6, background: "var(--white)", border: "1px solid var(--border-strong)", borderRadius: 999, padding: "14px 18px", marginBottom: 14, cursor: "pointer", fontFamily: SANS, textAlign: "left" },
  leaderPillEyebrow: { flexShrink: 0, fontSize: 12, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--gold-ink)" },
  leaderPillSummaryLight: { flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  leaderPillChevronLight: { flexShrink: 0, fontSize: 16, color: "var(--stone)" },

  // Dark plan card (docs/FIX-PLANS.md §1h, designs/PlanCardDark.png option C)
  // — no day title or passage here; those show once you tap in.
  darkPlanCard: { background: "#1F1B16", borderRadius: 20, padding: "13px 14px", display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 },
  darkKicker: { fontSize: 11, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: GOLD },
  darkKickerSub: { fontWeight: 600, opacity: 0.75, textTransform: "none", color: "#fff" },
  darkTitleRow: { display: "flex", alignItems: "center", gap: 10 },
  darkTitle: { flex: 1, minWidth: 0, fontSize: 19, fontWeight: 800, color: "#fff", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  darkOpenBtn: { flexShrink: 0, background: GOLD, color: "var(--ink)", border: "none", borderRadius: 999, padding: "9px 16px", fontSize: 13.5, fontWeight: 800, cursor: "pointer", fontFamily: SANS },
  darkOpenBtnOutline: { background: "transparent", color: GOLD, boxShadow: `0 0 0 1.5px ${GOLD}` },
  darkBar: { display: "flex", gap: 4 },
  darkBarSegment: { flex: 1, height: 8, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center" },
  darkBarSegmentDone: { background: GOLD },
  darkBarSegmentCurrent: { background: "transparent", boxShadow: `0 0 0 1.5px ${GOLD}` },
  darkBarSegmentFuture: { background: "rgba(255,255,255,0.14)" },
  darkBarTick: { fontSize: 7, fontWeight: 900, color: "#1F1B16" },
  darkStatusRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 },
  darkStatusLine: { fontSize: 12.5, fontWeight: 700, color: "rgba(255,255,255,0.8)" },
  darkStatusFaces: { fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.7)" },

  // "Left this plan, no other plan" — shrinks to a slim white row (same
  // section, different state).
  planLeftRow: { display: "flex", alignItems: "center", gap: 10, background: "var(--white)", borderRadius: 18, padding: "14px 16px", marginBottom: 14 },
  planLeftKicker: { fontSize: 10.5, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--gold-ink)" },
  planLeftTitle: { fontSize: 14.5, fontWeight: 800, color: "var(--ink)", marginTop: 2 },
  planLeftJoinBtn: { flexShrink: 0, background: "transparent", border: "1.5px solid var(--border-strong)", color: "var(--ink)", borderRadius: 999, padding: "9px 16px", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: SANS },

  // "Catch up on Sunday" — separate white row under the plan card.
  catchUpRow: { width: "100%", display: "flex", gap: 12, alignItems: "center", background: "var(--white)", border: "none", borderRadius: 18, padding: 12, marginBottom: 14, cursor: "pointer", textAlign: "left", fontFamily: SANS },
  sermonThumbWrap: { position: "relative", flexShrink: 0, width: 72, height: 72, borderRadius: 14, overflow: "hidden", background: "#1F1B16" },
  sermonThumb: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  playDot: { position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" },
  durationBadge: { position: "absolute", right: 4, bottom: 4, background: "rgba(0,0,0,0.65)", color: "#fff", fontSize: 9.5, fontWeight: 700, borderRadius: 5, padding: "1px 5px" },
  sermonTitle: { fontSize: 15, fontWeight: 800, color: "var(--ink)" },
  sermonMeta: { fontSize: 12, color: "var(--stone)", marginTop: 2 },
  catchUpChevron: { flexShrink: 0, fontSize: 18, color: "var(--stone)" },

  // Keep reading / Pause & pray — same vertical-stack structure, equal
  // 1fr/1fr width (docs/designs/ThisWeekFixed.png). Both use `tileIcon` below,
  // just a different background per side.
  tileIcon: { flexShrink: 0, width: 38, height: 38, borderRadius: 13, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, marginBottom: 10 },
  keepReadingCard: { flex: 1, borderRadius: 22, background: "var(--sky)", color: "var(--sky-ink)", padding: 14, display: "flex", flexDirection: "column", border: "none", cursor: "pointer", textAlign: "left", fontFamily: SANS, boxSizing: "border-box" },
  keepReadingTitle: { fontSize: 15, fontWeight: 800, color: "var(--ink)" },
  keepReadingSub: { fontSize: 12.5, color: "var(--sky-ink)", opacity: 0.85, marginTop: 2, lineHeight: 1.35 },

  prayCard: { flex: 1, borderRadius: 22, background: "var(--blush)", color: "var(--blush-ink)", padding: 14, border: "none", cursor: "pointer", textAlign: "left", fontFamily: SANS, display: "flex", flexDirection: "column", boxSizing: "border-box" },
  prayTitle: { fontSize: 15, fontWeight: 800, color: "var(--ink)" },
  praySub: { fontSize: 12.5, opacity: 0.85, lineHeight: 1.35, marginTop: 2 },
  readPassageRow: { width: "100%", display: "flex", alignItems: "center", gap: 10, background: "var(--white)", border: "none", borderRadius: 18, padding: "14px 16px", marginTop: 10, cursor: "pointer", fontFamily: SANS, textAlign: "left" },
  readPassagePill: { flexShrink: 0, border: "1.5px solid var(--ink)", borderRadius: 999, padding: "8px 14px", fontSize: 12.5, fontWeight: 800, color: "var(--ink)" },

  verseCard: { background: "var(--white)", borderRadius: 22, padding: 16, marginTop: 10, display: "flex", flexDirection: "column", gap: 8 },
  verseCardHead: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  verseCardLabel: { fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--gold-ink)" },
  verseIconBtn: { width: 32, height: 32, borderRadius: 16, border: "none", background: "var(--cream)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  verseCardText: { fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 18, lineHeight: 1.5, color: "var(--ink)" },
  verseCardRef: { fontSize: 13, fontWeight: 800, color: "var(--stone)" },

  eventsSection: { marginTop: 26 },
  eventsSectionHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 },
  noEventsCard: { display: "flex", gap: 12, alignItems: "flex-start", background: "var(--white)", borderRadius: 20, padding: 14 },
  eventsErrorCard: { background: "rgba(249,112,102,0.10)", border: "1px solid rgba(249,112,102,0.30)", borderRadius: 14, padding: "12px 14px", fontSize: 12.5, color: "#C8372A", fontFamily: SANS },
  noEventsIcon: { flexShrink: 0, width: 44, height: 44, borderRadius: 14, background: "var(--cream)", display: "flex", alignItems: "center", justifyContent: "center" },
  noEventsTitle: { fontSize: 14.5, fontWeight: 800, color: "var(--ink)" },
  noEventsSub: { fontSize: 12.5, color: "var(--stone)", lineHeight: 1.4, marginTop: 2 },
  sectionTitle: { fontSize: 11.5, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--gold-ink)" },
  addEventBtn: { flexShrink: 0, background: "transparent", border: "none", color: "var(--gold-ink)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS, marginBottom: 10 },
  cogBtn: { flexShrink: 0, width: 28, height: 28, borderRadius: 14, background: "transparent", border: "none", color: "var(--stone)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  cogBtnOn: { background: "var(--gold)", color: "var(--ink)" },
  seeAllEventsBtn: { display: "block", width: "100%", background: "transparent", border: "none", color: "var(--gold-ink)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS, textAlign: "center", padding: "6px 0 2px" },
};
